import type { UseCaseDetail, CadrageReadWithTeam, User, DomainWithDetails } from '../../types';
import type { CadrageStatusBreakdown } from '../../types/dashboard';
import { brandChartPalette } from '../../lib/branding';
import i18n from '../../i18n/config';
import { getUseCaseStatusLabel } from '../../components/UseCases/useCaseStatus';

/** Start of current week (Monday 00:00:00). */
export function getThisWeekStart(): Date {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - daysToSubtract);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
}

/** Count items whose created_at is >= start of current week. */
export function countCreatedThisWeek<T>(
  items: T[],
  getCreatedAt: (item: T) => string | null | undefined
): number {
  const start = getThisWeekStart();
  return items.filter((item) => {
    const createdAt = getCreatedAt(item);
    if (!createdAt) return false;
    return new Date(createdAt) >= start;
  }).length;
}

/** Aggregate use cases by status. */
export function buildUseCaseStats(useCases: { status: string }[]): Record<string, number> {
  return useCases.reduce((acc, useCase) => {
    acc[useCase.status] = (acc[useCase.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

export interface BarChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

export function buildBarChartData(useCaseStats: Record<string, number>): BarChartData {
  const labels = Object.keys(useCaseStats).map((status) => getUseCaseStatusLabel(status, i18n.t));
  return {
    labels,
    datasets: [
      {
        label: i18n.t('dashboard.useCasesByStatus'),
        data: Object.values(useCaseStats),
        backgroundColor: brandChartPalette(Math.max(labels.length, 1)),
      },
    ],
  };
}

/** Aggregate cadrages by type, normalising empty/null/undefined into a translated "no type" label. */
export function buildCadrageStats(cadrages: { type: string | null | undefined }[]): Record<string, number> {
  const noTypeLabel = i18n.t('dashboard.noCadrageType');
  return cadrages.reduce((acc, cadrage) => {
    const raw = (cadrage.type ?? '').toString().trim();
    const key = raw && raw.toLowerCase() !== 'undefined' && raw.toLowerCase() !== 'null'
      ? raw
      : noTypeLabel;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

export interface DoughnutChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

export function buildDoughnutChartData(cadrageStats: Record<string, number>): DoughnutChartData {
  const entries = Object.entries(cadrageStats).filter(([, v]) => v > 0);
  const hasData = entries.length > 0;
  const labels = hasData ? entries.map(([k]) => k) : [i18n.t('dashboard.noCadrages')];
  const data = hasData ? entries.map(([, v]) => v) : [1];
  const backgroundColor = brandChartPalette(labels.length);
  return {
    labels,
    datasets: [{ data, backgroundColor }],
  };
}

export interface TrafficItem {
  name: string;
  percentage: number;
  status?: string;
  id?: string;
}

export function buildLatestUseCasesData(
  useCases: { id: string; name: string; status: string }[],
  limit = 6
): TrafficItem[] {
  return useCases.slice(0, limit).map((useCase, index) => ({
    name: useCase.name,
    percentage: Math.max(100 - index * 15, 10),
    // Display-only field: render the translated status label.
    status: getUseCaseStatusLabel(useCase.status, i18n.t),
    id: useCase.id,
  }));
}

export interface Activity {
  id: string;
  type: 'user' | 'notification';
  message: string;
  timestamp: string;
}

export function buildActivities(
  useCases: UseCaseDetail[],
  cadrages: CadrageReadWithTeam[],
  limit = 5
): Activity[] {
  const fromUseCases = useCases
    .filter((uc) => uc.updated_at)
    .sort((a, b) => new Date(b.updated_at!).getTime() - new Date(a.updated_at!).getTime())
    .slice(0, 3)
    .map((useCase) => ({
      id: `uc-${useCase.id}`,
      type: 'user' as const,
      message: i18n.t('dashboard.activity.updatedUseCase', { name: useCase.name }),
      timestamp: new Date(useCase.updated_at!).toLocaleDateString(),
    }));

  const fromCadrages = cadrages
    .filter((c) => c.updated_at)
    .sort((a, b) => new Date(b.updated_at!).getTime() - new Date(a.updated_at!).getTime())
    .slice(0, 2)
    .map((cadrage) => ({
      id: `c-${cadrage.id}`,
      type: 'notification' as const,
      message: i18n.t('dashboard.activity.modifiedCadrage', { name: cadrage.name }),
      timestamp: new Date(cadrage.updated_at!).toLocaleDateString(),
    }));

  return [...fromUseCases, ...fromCadrages].slice(0, limit);
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export function buildContacts(members: User[], limit = 5): Contact[] {
  return members.slice(0, limit).map((member) => ({
    id: member.id,
    name: member.name || member.email,
    email: member.email,
    avatar: member.avatar,
  }));
}

export function buildLastModifiedDomains<T extends { id: string; updated_at?: string | null }>(
  domains: T[],
  limit = 2
): T[] {
  return domains
    .filter((d) => d.updated_at)
    .sort((a, b) => new Date(b.updated_at!).getTime() - new Date(a.updated_at!).getTime())
    .slice(0, limit);
}

const CADRAGE_STATUS_KEYS: (keyof CadrageStatusBreakdown)[] = [
  'draft', 'validated', 'planned', 'progress', 'forge', 'deployed'
];

/** Build cadrage status breakdown from the actual cadrages list (for correct dashboard totals). */
export function buildCadrageStatusBreakdown(
  cadrages: { status?: string | null }[]
): CadrageStatusBreakdown {
  const out: CadrageStatusBreakdown = {
    draft: 0,
    validated: 0,
    planned: 0,
    progress: 0,
    forge: 0,
    deployed: 0,
    total: cadrages.length,
  };
  for (const c of cadrages) {
    const s = (c.status || 'draft').toLowerCase();
    if (CADRAGE_STATUS_KEYS.includes(s as keyof CadrageStatusBreakdown) && s !== 'total') {
      (out as Record<string, number>)[s] = ((out as Record<string, number>)[s] || 0) + 1;
    } else {
      out.draft += 1;
    }
  }
  return out;
}
